import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import NavigationOutlinedIcon from '@mui/icons-material/NavigationOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { borderRadius, layoutGap, quickFillLayout } from '../../../../theme/theme';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { quickFillActionTooltips } from '../../../../shared/config';
import type { HelpValue } from '../../../../shared/ui/common/helpModel';

const MINIMAP_HEIGHT = 220;
const MINIMAP_WIDTH = 84;
const COLLAPSED_MINIMAP_HEIGHT = 72;
const COLLAPSED_MINIMAP_WIDTH = 24;
const MIN_BIN_COUNT = 36;
const MAX_BIN_COUNT = 160;

export interface QuickFillNavigationMarker {
  rowIndex: number;
  label: string;
  position: number | null;
  isVisible: boolean;
}

export interface QuickFillNavigationDockProps {
  collapsed: boolean;
  collapseLocked?: boolean;
  totalRows: number;
  filteredRowCount: number;
  rowCoverage: number[];
  currentPosition: number | null;
  viewportStart: number | null;
  viewportEnd: number | null;
  activeRowLabel: string | null;
  markers: QuickFillNavigationMarker[];
  hiddenMarkerCount: number;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
  onToggleCollapsed: () => void;
  onJumpToPosition: (position: number) => void;
  onJumpToActive: () => void;
  onToggleMarkerForActive: () => void;
  onRemoveMarker: (rowIndex: number) => void;
}

interface MinimapBin {
  index: number;
  start: number;
  end: number;
  coverage: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const createMinimapBins = (rowCoverage: number[], totalRows: number): MinimapBin[] => {
  if (totalRows === 0) {
    return [];
  }

  const binCount = clamp(totalRows, MIN_BIN_COUNT, MAX_BIN_COUNT);

  return Array.from({ length: binCount }, (_, index) => {
    const start = Math.floor((index / binCount) * totalRows);
    const end = Math.max(start + 1, Math.floor(((index + 1) / binCount) * totalRows));
    const sample = rowCoverage.slice(start, end);
    const coverage = sample.length > 0
      ? sample.reduce((sum, value) => sum + value, 0) / sample.length
      : 0;

    return { index, start, end, coverage };
  });
};

const toPercent = (value: number, total: number): number =>
  total <= 0 ? 0 : (value / total) * 100;

const rowCountLabel = (count: number): string =>
  `${count} row${count === 1 ? '' : 's'}`;

interface NavigationDockMinimapProps {
  height: number;
  width: number;
  rowCoverage: number[];
  filteredRowCount: number;
  currentPosition: number | null;
  viewportStart: number | null;
  viewportEnd: number | null;
  markers: QuickFillNavigationMarker[];
  onJumpToPosition: (position: number) => void;
}

const NavigationDockMinimap: React.FC<NavigationDockMinimapProps> = ({
  height,
  width,
  rowCoverage,
  filteredRowCount,
  currentPosition,
  viewportStart,
  viewportEnd,
  markers,
  onJumpToPosition,
}) => {
  const theme = useTheme();
  const minimapBins = useMemo(
    () => createMinimapBins(rowCoverage, filteredRowCount),
    [filteredRowCount, rowCoverage]
  );
  const currentPercent = currentPosition == null
    ? null
    : toPercent(currentPosition + 0.5, Math.max(filteredRowCount, 1));
  const viewportTop = viewportStart == null ? null : toPercent(viewportStart, Math.max(filteredRowCount, 1));
  const viewportBottom = viewportEnd == null ? null : toPercent(viewportEnd + 1, Math.max(filteredRowCount, 1));
  const viewportHeight = viewportTop != null && viewportBottom != null
    ? Math.max(viewportBottom - viewportTop, 4)
    : null;
  const visibleMarkers = markers.filter((marker) => marker.isVisible && marker.position != null);

  const handleMinimapClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (filteredRowCount === 0) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeY = clamp(event.clientY - bounds.top, 0, bounds.height);
    const ratio = bounds.height <= 0 ? 0 : relativeY / bounds.height;
    const targetPosition = clamp(Math.round(ratio * Math.max(filteredRowCount - 1, 0)), 0, Math.max(filteredRowCount - 1, 0));
    onJumpToPosition(targetPosition);
  };

  const handleMinimapKeyboardJump = () => {
    if (filteredRowCount === 0) {
      return;
    }

    if (currentPosition != null) {
      onJumpToPosition(currentPosition);
      return;
    }

    onJumpToPosition(clamp(Math.floor(filteredRowCount / 2), 0, Math.max(filteredRowCount - 1, 0)));
  };

  return (
    <ActionHelpTooltip help={quickFillActionTooltips.datasetMinimap} placement="top">
      <Box
        data-testid="quickfill-data-minimap"
        role="button"
        tabIndex={0}
        aria-label="Quick Fill dataset minimap"
        onClick={handleMinimapClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMinimapKeyboardJump();
          }
        }}
        sx={{
          position: 'relative',
          width,
          height,
          overflow: 'hidden',
          borderRadius: `${borderRadius.sm}px`,
          backgroundColor: alpha(theme.palette.background.default, 0.14),
          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
          cursor: 'pointer',
        }}
      >
        {minimapBins.map((bin) => {
          const top = toPercent(bin.index, minimapBins.length);
          const binHeight = Math.max(100 / Math.max(minimapBins.length, 1), 1.5);
          const intensity = 0.12 + bin.coverage * 0.78;

          return (
            <Box
              key={bin.index}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${top}%`,
                height: `${binHeight}%`,
                backgroundColor: alpha(theme.palette.primary.main, Math.max(0.08, intensity - 0.06)),
              }}
            />
          );
        })}

        {visibleMarkers.map((marker) => (
          <Tooltip key={marker.rowIndex} title={marker.label} placement="left">
            <Box
              sx={{
                position: 'absolute',
                left: -1,
                right: -1,
                top: `${toPercent((marker.position ?? 0) + 0.5, Math.max(filteredRowCount, 1))}%`,
                height: 2,
                transform: 'translateY(-50%)',
                backgroundColor: theme.palette.warning.light,
                boxShadow: `0 0 0 1px ${alpha(theme.palette.background.paper, 0.9)}`,
              }}
            />
          </Tooltip>
        ))}

        {viewportTop != null && viewportHeight != null ? (
          <Box
            sx={{
              position: 'absolute',
              left: 4,
              right: 4,
              top: `${viewportTop}%`,
              height: `${viewportHeight}%`,
              borderRadius: `${borderRadius.sm}px`,
              border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.08),
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.32)}`,
              pointerEvents: 'none',
            }}
          />
        ) : null}

        {currentPercent != null ? (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${currentPercent}%`,
              height: 2,
              transform: 'translateY(-50%)',
              backgroundColor: theme.palette.warning.light,
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
    </ActionHelpTooltip>
  );
};

interface NavigationDockMarkerListProps {
  markers: QuickFillNavigationMarker[];
  filteredRowCount: number;
  onJumpToPosition: (position: number) => void;
  onRemoveMarker: (rowIndex: number) => void;
}

const NavigationDockMarkerList: React.FC<NavigationDockMarkerListProps> = ({
  markers,
  filteredRowCount,
  onJumpToPosition,
  onRemoveMarker,
}) => {
  if (markers.length === 0) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack direction="row" spacing={layoutGap.flush} useFlexGap flexWrap="wrap">
        {markers.map((marker) => (
          <Tooltip
            key={marker.rowIndex}
            title={`Jump to ${marker.label}. Delete removes this marker from the navigator.`}
            placement="top"
            describeChild
          >
            <Chip
              label={marker.label}
              onClick={marker.position != null ? () => onJumpToPosition(marker.position ?? 0) : undefined}
              onDelete={() => onRemoveMarker(marker.rowIndex)}
              size="small"
              variant="outlined"
              sx={{
                maxWidth: '100%',
                height: 16,
                borderRadius: `${borderRadius.none}px`,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    </>
  );
};

interface NavigationDockControlsProps {
  variant: 'open' | 'collapse' | 'actions';
  collapseLocked?: boolean;
  canJumpToActive?: boolean;
  canToggleMarker?: boolean;
  activeRowMarked?: boolean;
  onToggleCollapsed: () => void;
  onJumpToActive?: () => void;
  onToggleMarkerForActive?: () => void;
}

const NavigationDockControls: React.FC<NavigationDockControlsProps> = ({
  variant,
  collapseLocked = false,
  canJumpToActive = false,
  canToggleMarker = false,
  activeRowMarked = false,
  onToggleCollapsed,
  onJumpToActive,
  onToggleMarkerForActive,
}) => {
  const theme = useTheme();

  if (variant === 'open') {
    return (
      <ActionHelpTooltip help={quickFillActionTooltips.navigatorOpen} placement="top">
        <IconButton
          size="small"
          onClick={onToggleCollapsed}
          aria-label="Open dataset navigator"
          sx={{ color: theme.palette.primary.main }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      </ActionHelpTooltip>
    );
  }

  if (variant === 'collapse') {
    return (
      <ActionHelpTooltip
        help={quickFillActionTooltips.navigatorCollapse}
        disabledHelp={quickFillActionTooltips.navigatorCollapseLocked}
        disabled={collapseLocked}
        placement="top"
      >
        <IconButton
          size="small"
          onClick={onToggleCollapsed}
          aria-label="Collapse dataset navigator"
          disabled={collapseLocked}
          sx={{ color: theme.palette.text.secondary }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </ActionHelpTooltip>
    );
  }

  const markerToggleHelp: HelpValue = activeRowMarked
    ? quickFillActionTooltips.navigatorRemoveMarker
    : quickFillActionTooltips.navigatorAddMarker;

  return (
    <Stack direction="row" spacing={layoutGap.comfort}>
      <ActionHelpTooltip
        help={quickFillActionTooltips.navigatorFocus}
        disabledHelp={quickFillActionTooltips.navigatorFocusDisabled}
        disabled={!canJumpToActive}
        placement="top"
        fullWidth
        wrapperSx={{ flex: 1, minWidth: 0 }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={onJumpToActive}
          disabled={!canJumpToActive}
          aria-label="Jump to current location"
          sx={{
            width: '100%',
            minWidth: 0,
            px: 0.65,
            py: layoutGap.micro,
            fontSize: '0.66rem',
            borderRadius: `${borderRadius.none}px`,
            boxShadow: 'none',
          }}
        >
          Focus
        </Button>
      </ActionHelpTooltip>
      <ActionHelpTooltip
        help={markerToggleHelp}
        disabledHelp={quickFillActionTooltips.navigatorMarkerDisabled}
        disabled={!canToggleMarker}
        placement="top"
        fullWidth
        wrapperSx={{ flex: 1, minWidth: 0 }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={onToggleMarkerForActive}
          disabled={!canToggleMarker}
          aria-label={activeRowMarked ? 'Remove marker' : 'Add marker'}
          sx={{
            width: '100%',
            minWidth: 0,
            px: 0.65,
            py: layoutGap.micro,
            fontSize: '0.66rem',
            borderRadius: `${borderRadius.none}px`,
            boxShadow: 'none',
          }}
        >
          {activeRowMarked ? 'Unmark' : 'Mark'}
        </Button>
      </ActionHelpTooltip>
    </Stack>
  );
};

const QuickFillNavigationDock: React.FC<QuickFillNavigationDockProps> = ({
  collapsed,
  collapseLocked = false,
  totalRows,
  filteredRowCount,
  rowCoverage,
  currentPosition,
  viewportStart,
  viewportEnd,
  activeRowLabel,
  markers,
  hiddenMarkerCount,
  activeRowMarked,
  canJumpToActive,
  canToggleMarker,
  onToggleCollapsed,
  onJumpToPosition,
  onJumpToActive,
  onToggleMarkerForActive,
  onRemoveMarker,
}) => {
  const theme = useTheme();

  const activeLocationText = currentPosition == null
    ? 'No current location'
    : `Focused around row ${currentPosition + 1}`;

  if (collapsed) {
    return (
      <Paper
        data-testid="quickfill-navigation-dock"
        elevation={8}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: layoutGap.row,
          px: 0.75,
          py: 0.65,
          borderRadius: `${borderRadius.none}px`,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
          boxShadow: 'none',
        }}
      >
        <NavigationDockMinimap
          height={COLLAPSED_MINIMAP_HEIGHT}
          width={COLLAPSED_MINIMAP_WIDTH}
          rowCoverage={rowCoverage}
          filteredRowCount={filteredRowCount}
          currentPosition={currentPosition}
          viewportStart={viewportStart}
          viewportEnd={viewportEnd}
          markers={markers}
          onJumpToPosition={onJumpToPosition}
        />
        <Stack spacing={layoutGap.tight}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Navigator
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.66rem' }}>
            {rowCountLabel(filteredRowCount || totalRows)}
          </Typography>
        </Stack>
        <NavigationDockControls
          variant="open"
          onToggleCollapsed={onToggleCollapsed}
        />
      </Paper>
    );
  }

  return (
    <Paper
      data-testid="quickfill-navigation-dock"
      elevation={10}
      sx={{
        width: quickFillLayout.sidePanelWidth,
        maxWidth: `min(${quickFillLayout.sidePanelWidth}px, calc(100vw - 12px))`,
        overflow: 'hidden',
        borderRadius: `${borderRadius.none}px`,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 0.9,
          py: 0.7,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
        }}
      >
        <Stack spacing={layoutGap.tight} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
            <NavigationOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Navigator
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {rowCountLabel(filteredRowCount || totalRows)}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, lineHeight: 1.3 }}>
            Focus and markers
          </Typography>
        </Stack>
        <NavigationDockControls
          variant="collapse"
          collapseLocked={collapseLocked}
          onToggleCollapsed={onToggleCollapsed}
        />
      </Box>

      <Stack spacing={layoutGap.flow} sx={{ p: 0.85 }}>
        <Stack direction="row" spacing={layoutGap.flow} alignItems="stretch">
          <Box sx={{ flexShrink: 0 }}>
            <NavigationDockMinimap
              height={MINIMAP_HEIGHT}
              width={MINIMAP_WIDTH}
              rowCoverage={rowCoverage}
              filteredRowCount={filteredRowCount}
              currentPosition={currentPosition}
              viewportStart={viewportStart}
              viewportEnd={viewportEnd}
              markers={markers}
              onJumpToPosition={onJumpToPosition}
            />
          </Box>

          <Stack spacing={layoutGap.blend} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={layoutGap.comfort} useFlexGap flexWrap="wrap" alignItems="center">
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {activeLocationText}
              </Typography>
              {activeRowMarked ? (
                <ActionHelpTooltip help={quickFillActionTooltips.navigatorMarked} placement="top">
                  <Chip
                    size="small"
                    label="Marked"
                    variant="outlined"
                    sx={{ height: 16, borderRadius: `${borderRadius.none}px`, fontWeight: 600 }}
                  />
                </ActionHelpTooltip>
              ) : null}
              {hiddenMarkerCount > 0 ? (
                <ActionHelpTooltip help={quickFillActionTooltips.navigatorHiddenMarkers} placement="top">
                  <Chip
                    size="small"
                    label={`${hiddenMarkerCount} hidden`}
                    variant="outlined"
                    sx={{ height: 16, borderRadius: `${borderRadius.none}px`, fontWeight: 600 }}
                  />
                </ActionHelpTooltip>
              ) : null}
            </Stack>

            <Typography
              variant="body2"
              sx={{
                color: activeRowLabel ? theme.palette.text.primary : theme.palette.text.secondary,
                minHeight: 24,
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1.4,
                fontSize: '0.72rem',
              }}
            >
              {activeRowLabel || 'Scroll or select a row to see where you are in the dataset.'}
            </Typography>

            <NavigationDockControls
              variant="actions"
              canJumpToActive={canJumpToActive}
              canToggleMarker={canToggleMarker}
              activeRowMarked={activeRowMarked}
              onToggleCollapsed={onToggleCollapsed}
              onJumpToActive={onJumpToActive}
              onToggleMarkerForActive={onToggleMarkerForActive}
            />

            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Click the minimap to jump.
            </Typography>
          </Stack>
        </Stack>

        <NavigationDockMarkerList
          markers={markers}
          filteredRowCount={filteredRowCount}
          onJumpToPosition={onJumpToPosition}
          onRemoveMarker={onRemoveMarker}
        />
      </Stack>
    </Paper>
  );
};

export default QuickFillNavigationDock;
